<template>
  <div>
    <h2>Should you use watch or computed?</h2>
    <br />
    <p>
      Watched props can often be confused with computed properties, because they
      operate in a similar way. It's even trickier to know when to use which
      one.
      <b
        >Watch is for side effects. If you need to change state you want to use
        a computed prop instead.</b
      >
      A side effect is anything that happens outside of your component, or
      anything asynchronous.
    </p>
    <ul>
      <li>Fetching data</li>
      <li>Manipulating the DOM</li>
      <li>Using a browser API, such as local storage or audio playback</li>
    </ul>
    <br />

    <h3>Watching nested data — Arrays and Objects</h3>
    <br />
    <p>Watchers in Vue let you specify three different properties:</p>
    <ul>
      <li>deep</li>
      <li>immediate</li>
      <li>handler</li>
    </ul>
    <br />
    <h3>Deep</h3>
    <p>
      There is also an attribute deep in the watch. The default value is false,
      which indicates whether to listen in depth. When an object needs to be
      monitored for changes, the normal watch method cannot monitor the changes
      of the internal attributes of the object. Only the data in the data can
      monitor the changes. At this time, the deep attribute is required to
      monitor the object in depth.
      <!-- This will let Vue know to look inside the array -->
    </p>

    <h3>Immediate</h3>
    <p>
      When the parent component dynamically transfers values to the child
      component, when the child component props gets the default value from the
      parent component for the first time, it also needs to execute the
      function. In this case, you need to set immediate to true.
      <!-- A watcher will only fire when the prop's value changes, but we often need it to fire once on startup as well. -->
    </p>

    <h3>Handler</h3>
    <p>
      The property handler specifies the function that will be called when the
      watched prop changes. We have to move our method to a handler field.
      Immediate:true If viewdetails is declared in watch, the handler method
      will be executed immediately. If it is false, it will be the same as our
      previous effect. It will not be executed when binding
    </p>
    <pre>
      <code>&lt;template&gt;
  &lt;v-container&gt;
    &lt;v-row&gt;
      &lt;v-col cols="12" md="6"&gt;
        &lt;v-card dark class="pa-5"&gt;
          &lt;v-row&gt;
              {{inputLoopString}}
            &lt;/v-row&gt;
          &lt;/v-card&gt;
        &lt;/v-col&gt;
        &lt;v-col cols="12" md="6"&gt;
          &lt;v-card dark&gt;
            {{listLoopString}}
          &lt;/v-card&gt;
        &lt;/v-col&gt;
      &lt;/v-row&gt;
    &lt;/v-container&gt;
&lt;/template&gt;
&lt;script&gt;
  export default {
    name: "WatcherUsage", 
    data() {
      return {
        people: [
          { id: 0, name: "Tom Cruise", age: 59 },
          { id: 1, name: "Brad Pitt", age: 57 },
          { id: 2, name: "Angelina Jolie", age: 46 },
        ],
      }
    },
    watch: {
      people: {
        handler(newVal, oldVal) {
          // Return the object that changed
          newVal.filter((p, idx) => {
            return Object.keys(p).some((prop) => {
              return p[prop] !== oldVal[idx][prop];
            });
          });
        },
        deep: true,
        immediate: true,
      },
    },
  };
&lt;/script&gt;</code>
    </pre>

    <h2 class="ml-2">Output</h2>
    <v-container>
      <v-row>
        <v-col cols="12" md="6">
          <v-card dark class="pa-5">
            <v-row>
              <v-col
                cols="12"
                md="4"
                v-for="(person, index) in people"
                :key="person"
              >
                <v-text-field
                  v-model="people[index].age"
                  :label="people[index].name"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card dark>
            <v-list-item v-for="person in people" :key="person">
              <v-list-item-title>Age of {{ person.name }} is <v-chip label :ripple="false" outlined>{{ person.age }}</v-chip></v-list-item-title>
            </v-list-item>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
export default {
  name: "WatcherUsage",
  data() {
    return {
      people: [
        { id: 0, name: "Tom Cruise", age: 59 },
        { id: 1, name: "Brad Pitt", age: 57 },
        { id: 2, name: "Angelina Jolie", age: 46 },
      ],
      listLoopString: `<v-list-item v-for="person in people" :key="person">
              <v-list-item-title>{{ person.age }} - is the age of {{ person.name }}</v-list-item-title>
            </v-list-item>`,
      inputLoopString: `<v-col cols="12" md="4" v-for="(person, index) in people" :key="person" >
                <v-text-field
                  v-model="people[index].age"
                  :label="people[index].name"
                ></v-text-field>
              </v-col>`,
    };
  },
  watch: {
    people: {
      handler(newVal, oldVal) {
        // Return the object that changed
        newVal.filter((p, idx) => {
          return Object.keys(p).some((prop) => {
            return p[prop] !== oldVal[idx][prop];
          });
        });
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
